import { Anchor, Checkbox, Flex, Text, Title } from '@mantine/core'
import { useMemo, useState } from 'react'

import { Trans, useTranslation } from '@flecto/translations'
import { CardList, type CardListProps } from '@flecto/ui'

import { useOnboardingNavigation } from '../../hooks/useOnboardingNavigation'
import { useOnboardingSelectOptions } from '../../hooks/useOnboardingSelectOptions'
import { useFormContext } from './FormOnboarding'
import { StepWrapper } from './StepWrapper'
import classes from './styles.module.css'

export function StepReview() {
  const [accepted, setAccepted] = useState(false)
  const { t, i18n } = useTranslation('saas', { keyPrefix: 'onboarding' })
  const { nextStep } = useOnboardingNavigation()

  const links = useMemo(() => {
    switch (i18n.language) {
      case 'en':
      case 'es':
        return {
          tc: 'https://flecto.io/legal/terms-and-conditions',
          privacy: 'https://flecto.io/legal/privacy-policy',
        }

      default:
      case 'pt':
        return {
          tc: 'https://flecto.io/pt/legal/terms-and-conditions',
          privacy: 'https://flecto.io/pt/legal/privacy-policy',
        }
    }
  }, [i18n.language])

  const LabelAcceptTC = () => (
    <Text fw={500}>
      <Trans
        ns='saas'
        i18nKey='onboarding.steps.review_and_start.accept_terms_and_conditions'
        components={[
          <Anchor key='link-tc' href={links.tc} target='_blank' />,
          <Anchor key='link-privacy' href={links.privacy} target='_blank' />,
        ]}
      />
    </Text>
  )

  return (
    <StepWrapper
      stepNumber={4}
      title={t('steps.review_and_start.title')}
      description={t('steps.review_and_start.description')}
      onNextStep={() => nextStep()}
      buttonNextProps={{
        disabled: !accepted,
        tooltip: !accepted
          ? t('steps.review_and_start.accept_terms_and_conditions_tooltip')
          : undefined,
      }}
    >
      <CardReviewStep01 />
      <CardReviewStep02 />
      <CardReviewStep03 />
      <Checkbox
        mt='xl'
        mb='xl'
        checked={accepted}
        onChange={(e) => setAccepted(e.currentTarget.checked)}
        label={<LabelAcceptTC />}
      />
    </StepWrapper>
  )
}

type CardStepReviewProps = CardListProps & {
  step: number
  title: string
  data: { label: string; value: string | number | undefined }[]
}

function CardStepReview({ step, title, data, ...props }: CardStepReviewProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'onboarding' })
  const { moveToStep } = useOnboardingNavigation()

  return (
    <CardList {...props} className={classes.cardReview}>
      <CardList.Item>
        <Flex align='baseline' gap='md' mt='sm'>
          <Title lh={1} order={5} textWrap='nowrap'>
            {title}
          </Title>
          <Anchor lh={1} onClick={() => moveToStep(step)} c='blue'>
            {t('actions.edit')}
          </Anchor>
        </Flex>
      </CardList.Item>
      {data.map((item, index) => (
        <CardList.Item key={index}>
          <CardList.ItemLabel>{item.label}</CardList.ItemLabel>
          <CardList.ItemValue>{item.value}</CardList.ItemValue>
        </CardList.Item>
      ))}
    </CardList>
  )
}

function CardReviewStep01() {
  const { t } = useTranslation('saas', { keyPrefix: 'onboarding' })
  const { qtyEmployees } = useOnboardingSelectOptions()
  const form = useFormContext()

  const qtyEmployeesLabel = useMemo(() => {
    const item = qtyEmployees.find(
      (item) => item.value === form.values.step_basic_info.qty_employees
    )

    return item?.label
  }, [form.values])

  const user = form.getValues().step_basic_info

  const data = [
    {
      label: t('form_labels.first_name'),
      value: `${user.first_name} ${user.last_name}`,
    },
    {
      label: t('form_labels.company_name'),
      value: user.company_name,
    },
    {
      label: t('form_labels.qty_employees'),
      value: qtyEmployeesLabel,
    },
  ]

  return (
    <CardStepReview title={t('steps.basic_info.title')} step={1} data={data} />
  )
}

function CardReviewStep02() {
  const { t } = useTranslation('saas', { keyPrefix: 'onboarding' })
  const form = useFormContext()

  const data = [
    {
      label: t('form_labels.country_operation'),
      value: form.values.step_location.address.address_country_name,
    },
    {
      label: t('form_labels.zip_code'),
      value: form.values.step_location.address.address_postal_code,
    },
    {
      label: t('form_labels.street'),
      value: form.values.step_location.address.address_street,
    },
    {
      label: t('form_labels.city'),
      value: form.values.step_location.address.address_city,
    },
  ]

  return (
    <CardStepReview
      title={t('steps.main_location.title')}
      step={2}
      data={data}
      mt='xl'
    />
  )
}

function CardReviewStep03() {
  const { t } = useTranslation('saas', { keyPrefix: 'onboarding' })
  const {
    avgBookingsPerMonth,
    businessCategories,
    systemsToReplace,
    flectoProductsInterestedOn,
  } = useOnboardingSelectOptions()

  const form = useFormContext()

  const labels = useMemo(() => {
    const avgBookingsPerMonthItem = avgBookingsPerMonth.find(
      (item) =>
        item.value === form.values.step_business_info.avg_bookings_per_month
    )

    const businessCategoryItem = businessCategories.data.find(
      (item) => item.value === form.values.step_business_info.business_category
    )

    const systemsToReplaceItems = systemsToReplace
      .filter((item) =>
        form.values.step_business_info.systems_to_replace.includes(item.value)
      )
      .map((item) => item.label)

    const flectoProductsInterestedOnItems = flectoProductsInterestedOn
      .filter((item) =>
        form.values.step_business_info.flecto_products_interested_on.includes(
          item.value
        )
      )
      .map((item) => item.label)

    return {
      avgBookingsPerMonthLabel: avgBookingsPerMonthItem?.label ?? '-',
      businessCategoryLabel: businessCategoryItem?.label ?? '-',
      systemsToReplaceLabel: systemsToReplaceItems.join(', '),
      flectoProductsInterestedOnLabel:
        flectoProductsInterestedOnItems.join(', '),
    }
  }, [form.values, businessCategories])

  const data = [
    {
      label: t('form_labels.business_category'),
      value: labels.businessCategoryLabel,
    },
    {
      label: t('form_labels.avg_bookings_per_month'),
      value: labels.avgBookingsPerMonthLabel,
    },
    {
      label: t('form_labels.systems_to_replace'),
      value: labels.systemsToReplaceLabel,
    },
    {
      label: t('form_labels.flecto_products_interested_on'),
      value: labels.flectoProductsInterestedOnLabel,
    },
  ]

  return (
    <CardStepReview
      title={t('steps.business_info.title')}
      step={3}
      data={data}
      mt='xl'
    />
  )
}
